import React, { useEffect, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  initSWUpdates,
  subscribeUpdateAvailable,
  applyUpdate,
  dismissUpdate,
} from '../utils/swUpdate';

/**
 * PWAUpdateBanner — iter307
 *
 * Bottom toast that surfaces when a new service-worker build is waiting.
 * Strict scope:
 *   • Non-blocking, dismissible.
 *   • Does NOT auto-dismiss. Stays until user taps "Update now" or "Later".
 *   • Bilingual EN/AR with proper RTL direction.
 *   • Sits above the BottomNav (72px + safe area) and above the floating "+".
 *   • Reload is delegated to swUpdate.js (controllerchange handler reloads
 *     exactly once, guarded by sessionStorage).
 *
 * Does NOT touch:
 *   - PWAInstallBanner (separate concern, separate dismiss key)
 *   - Push subscription flow (pushNotifications.js still owns that)
 *   - Wallet / boost / student-hub / CV / messages / login / legal pages
 */
export default function PWAUpdateBanner() {
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';
  const [visible, setVisible] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    // Boot the SW update listener exactly once for the app session.
    initSWUpdates();
    const unsub = subscribeUpdateAvailable(({ available }) => {
      if (available) setVisible(true);
    });
    // QA-only hook for capturing screenshots in preview/dev. Stripped
    // by CRA build optimizer when NODE_ENV === 'production'.
    if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
      window.__wassla_pwa_update_show = () => setVisible(true);
    }
    return () => { unsub(); };
  }, []);

  if (!visible) return null;

  const onUpdate = () => {
    setApplying(true);
    const sent = applyUpdate();
    if (!sent) {
      // No waiting worker (race) — just hide the prompt.
      setVisible(false);
      setApplying(false);
    }
    // On success, the controllerchange handler in swUpdate.js will
    // reload the page exactly once. We keep "applying" state until then.
  };

  const onLater = () => {
    dismissUpdate();
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="pwa-update-banner"
      dir={isArabic ? 'rtl' : 'ltr'}
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        // Sits above BottomNav (72px) and safe-area inset. We add 16px so
        // the toast clearly clears the floating "+" button as well.
        bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
        zIndex: 1100, // above PWAInstallBanner (1000) — update is more urgent
        background: '#0f172a',
        color: '#fff',
        border: '1px solid #1e293b',
        borderRadius: 14,
        boxShadow: '0 16px 36px rgba(15, 23, 42, 0.32)',
        padding: 14,
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        maxWidth: 640,
        marginInline: 'auto',
      }}
    >
      <div
        style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: '#1F6F3A',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        aria-hidden="true"
      >
        <RefreshCw size={20} color="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          data-testid="pwa-update-title"
          style={{ fontWeight: 700, fontSize: 14, lineHeight: 1.35 }}
        >
          {isArabic ? 'إصدار جديد متاح' : 'New version available'}
        </div>
        <div
          style={{
            fontSize: 12.5,
            color: '#cbd5e1',
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          {isArabic
            ? 'حدّث الآن للحصول على أحدث التحسينات.'
            : 'Update now to get the latest improvements.'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={onUpdate}
            disabled={applying}
            data-testid="pwa-update-now-btn"
            style={{
              background: '#f97316',
              color: '#fff',
              border: 0,
              padding: '8px 14px',
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              cursor: applying ? 'wait' : 'pointer',
              opacity: applying ? 0.85 : 1,
            }}
          >
            <RefreshCw size={14} />
            {applying
              ? (isArabic ? 'جارٍ التحديث…' : 'Updating…')
              : (isArabic ? 'حدّث الآن' : 'Update now')}
          </button>
          <button
            type="button"
            onClick={onLater}
            disabled={applying}
            data-testid="pwa-update-later-btn"
            style={{
              background: 'transparent',
              color: '#cbd5e1',
              border: 0,
              padding: '8px 10px',
              borderRadius: 10,
              fontWeight: 600,
              fontSize: 13,
              cursor: applying ? 'wait' : 'pointer',
            }}
          >
            {isArabic ? 'لاحقاً' : 'Later'}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onLater}
        disabled={applying}
        aria-label={isArabic ? 'إغلاق' : 'Close'}
        data-testid="pwa-update-close"
        style={{
          background: 'transparent',
          border: 0,
          color: '#94a3b8',
          cursor: applying ? 'wait' : 'pointer',
          padding: 4,
          alignSelf: 'flex-start',
        }}
      >
        <X size={18} />
      </button>
    </div>
  );
}
